import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const shouldClose = (container, target) =>
  !container || !container.contains(target);

const normalize = (v) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

const isItemSelected = (selectedValues, item) => {
  const v = item?.value ?? item?.key ?? item?.label;
  return selectedValues.includes(v);
};

const getItemValue = (item) => item?.value ?? item?.key ?? item?.label;

const DropdownMenu = ({
  title,
  items,
  icon,
  isDropdown,
  onClick,
  withSearch = false,
  searchPlaceholder = "Rechercher...",
  maxMenuHeightClass = "max-h-72",
  multiselect = false,
  selectedValues = [],
  onSelectionChange,
  closeOnSelect = true,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const filteredItems = useMemo(() => {
    if (!withSearch) return items || [];
    const q = normalize(search);
    if (!q) return items || [];
    return (items || []).filter((item) => normalize(item.label).includes(q));
  }, [items, search, withSearch]);

  const toggleSelection = (item) => {
    const value = getItemValue(item);
    const exists = selectedValues.includes(value);
    const updated = exists
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange && onSelectionChange(updated);
  };

  const handleItemClick = (item) => {
    if (multiselect) return toggleSelection(item);

    if (closeOnSelect) setDropdownOpen(false);
    setSearch("");
    if (item.onClick) return item.onClick();
    if (item.href) navigate(item.href);
  };

  const handleButtonClick = () => {
    if (!isDropdown) return onClick && onClick();
    setDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shouldClose(dropdownRef.current, event.target)) {
        setDropdownOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) setSearch("");
  }, [isDropdownOpen]);

  return (
    <div className="relative w-full text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleButtonClick}
        className="w-full h-12 flex items-center justify-between gap-2 border rounded-md px-4 bg-white shadow-md transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300"
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className="flex items-center">{icon}</span>}
          <span className="text-gray-800 truncate">{title}</span>
        </div>

        {isDropdown && (
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-500 transition-transform flex-shrink-0 ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {isDropdown && isDropdownOpen && (
        <div className="absolute mt-2 right-0 w-full bg-white border border-gray-200 shadow-lg rounded-md z-10 overflow-hidden">
          {withSearch && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full h-10 border border-gray-200 rounded-md pl-9 pr-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className={`overflow-y-auto ${maxMenuHeightClass}`}>
            {filteredItems.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                Aucun résultat
              </div>
            ) : (
              filteredItems.map((item, index) => {
                const selected = multiselect
                  ? isItemSelected(selectedValues, item)
                  : false;

                return (
                  <button
                    type="button"
                    key={item.key ?? item.value ?? item.label ?? index}
                    onClick={() => handleItemClick(item)}
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 transition"
                  >
                    <div className="flex items-center gap-3">
                      {multiselect && (
                        <input
                          type="checkbox"
                          className="checkbox-pro"
                          checked={selected}
                          readOnly
                        />
                      )}
                      <span className="truncate">{item.label}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {multiselect && (
            <div className="p-2 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(false);
                  setSearch("");
                }}
                className="btn btn-sm"
              >
                Fermer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
